import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Heart, MapPin } from 'lucide-react';
import { getStats, getHearts, getFileUrl } from '@/lib/api';
import { cityConfig } from '@/config/cities';

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return 'hier';
  const days = Math.floor(diff / 86400);
  if (days < 7) return `il y a ${days}j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const TOTAL = cityConfig.totalHearts;
const GRID_COLS = cityConfig.gridCols;
const GRID_ROWS = cityConfig.gridRows;

const computeHeartPositions = () => {
  const positions = [];
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) {
      const x = (c / (GRID_COLS - 1)) * 2.6 - 1.3;
      const y = 1.5 - (r / (GRID_ROWS - 1)) * 2.7;
      const val = Math.pow(x * x + y * y - 1, 3) - x * x * Math.pow(y, 3);
      if (val <= 0) positions.push({ row: r, col: c });
    }
  }
  if (positions.length > TOTAL) {
    const centerCol = Math.floor(GRID_COLS / 2);
    const scored = positions.map((p, i) => ({ ...p, idx: i, score: p.row * 2 + Math.abs(p.col - centerCol) }));
    scored.sort((a, b) => a.score - b.score);
    const toRemove = new Set(scored.slice(0, positions.length - TOTAL).map(s => s.idx));
    return positions.filter((_, i) => !toRemove.has(i));
  }
  return positions.slice(0, TOTAL);
};

const HEART_POSITIONS = computeHeartPositions();
const minRow = Math.min(...HEART_POSITIONS.map(p => p.row));
const maxRow = Math.max(...HEART_POSITIONS.map(p => p.row));
const minCol = Math.min(...HEART_POSITIONS.map(p => p.col));
const maxCol = Math.max(...HEART_POSITIONS.map(p => p.col));

const HeartsGrid = () => {
  const navigate = useNavigate();
  const [hearts, setHearts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [heartsData, statsData] = await Promise.all([getHearts(), getStats()]);
        setHearts(heartsData);
        setStats(statsData);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    loadData();
  }, []);

  const heartsMap = useMemo(() => {
    const map = {};
    hearts.forEach(h => { map[h.heart_number] = h.verification_status || 'unverified'; });
    return map;
  }, [hearts]);

  // All discoveries for the bottom panel
  const recentDiscoveries = useMemo(() => {
    return [...hearts]
      .sort((a, b) => new Date(b.discovered_at) - new Date(a.discovered_at));
  }, [hearts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#E63946]" />
      </div>
    );
  }

  const cellSize = 10;
  const pad = 2;
  const vbX = (minCol * cellSize) - pad;
  const vbY = (minRow * cellSize) - pad;
  const vbW = ((maxCol - minCol) * cellSize) + cellSize + pad * 2;
  const vbH = ((maxRow - minRow) * cellSize) + cellSize + pad * 2;

  const discovered = stats?.total_discovered || 0;
  const verified = hearts.filter(h => h.verification_status === 'verified').length;
  const remaining = TOTAL - discovered;

  return (
    <div className="px-2 pt-1 pb-4 overflow-y-auto" style={{ height: 'calc(100dvh - 56px - 80px)' }} data-testid="hearts-grid-page">
      <div className="max-w-2xl mx-auto w-full">

        {/* Heart SVG grid — simple heartbeat, NO click action */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="heart-grid-beat"
        >
          <svg
            viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
            className="w-full"
            data-testid="heart-grid-svg"
          >
            {HEART_POSITIONS.map((pos, i) => {
              const num = i + 1;
              const status = heartsMap[num];
              const cx = pos.col * cellSize + cellSize / 2;
              const cy = pos.row * cellSize + cellSize / 2;
              let fill, stroke, textFill;
              if (status === 'verified') {
                fill = '#E63946'; stroke = 'none'; textFill = '#FFFFFF';
              } else if (status) {
                fill = '#FFFFFF'; stroke = 'none'; textFill = '#0A0A0B';
              } else {
                fill = '#1A1A1E'; stroke = '#2A2A2E'; textFill = '#3F3F46';
              }
              const ht = 4.2, hb = 3.8, hw = 4.5;
              return (
                <g key={num} data-testid={`heart-cell-${num}`}>
                  <path
                    d={`M${cx} ${cy + hb} C${cx} ${cy + hb}, ${cx - hw} ${cy - 0.3}, ${cx - hw} ${cy - 1.8} C${cx - hw} ${cy - ht + 0.5}, ${cx - hw + 1.7} ${cy - ht}, ${cx - hw / 2.5} ${cy - ht} C${cx - 0.5} ${cy - ht}, ${cx} ${cy - ht + 1.5}, ${cx} ${cy - 2.5} C${cx} ${cy - ht + 1.5}, ${cx + 0.5} ${cy - ht}, ${cx + hw / 2.5} ${cy - ht} C${cx + hw - 1.7} ${cy - ht}, ${cx + hw} ${cy - ht + 0.5}, ${cx + hw} ${cy - 1.8} C${cx + hw} ${cy - 0.3}, ${cx} ${cy + hb}, ${cx} ${cy + hb}Z`}
                    fill={fill} stroke={stroke} strokeWidth="0.3"
                  />
                  <text x={cx} y={cy - 0.5} textAnchor="middle" dominantBaseline="central" fill={textFill} fontSize="3.5" fontWeight="700">{num}</text>
                </g>
              );
            })}
          </svg>
        </motion.div>

        {/* Stats panel — spacing from heart */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="px-2 mt-5 space-y-2"
        >
          {/* Stats row */}
          <div className="flex items-center justify-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <Heart className="w-3 h-3 text-[#E63946]" fill="#E63946" />
              <span className="text-white font-bold">{verified}</span>
              <span className="text-[#71717A]">vérifiés</span>
            </div>
            <span className="text-[#333]">·</span>
            <div className="flex items-center gap-1">
              <Heart className="w-3 h-3 text-white" fill="white" />
              <span className="text-white font-bold">{discovered - verified}</span>
              <span className="text-[#71717A]">déclarés</span>
            </div>
            <span className="text-[#333]">·</span>
            <div className="flex items-center gap-1">
              <span className="text-[#E63946] font-bold" data-testid="grid-stats">{discovered}</span>
              <span className="text-[#71717A]">/{TOTAL}</span>
            </div>
          </div>

          <p className="text-center text-[#71717A] text-[10px]">{remaining} cœurs restent à découvrir</p>

          {/* All discoveries list — same style as Landing */}
          {recentDiscoveries.length > 0 && (
            <div className="space-y-1 pt-1">
              <p className="text-[10px] uppercase tracking-widest text-[#52525B] font-semibold px-1">Toutes les découvertes</p>
              {recentDiscoveries.map((h) => {
                const isVerified = h.verification_status === 'verified';
                return (
                  <button
                    key={h.heart_number}
                    onClick={() => navigate(`/heart/${h.heart_number}`)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 bg-white/[0.06] backdrop-blur-sm border-l-2 border-[#E63946]/60 hover:border-[#E63946] hover:bg-white/[0.1] transition-all text-left rounded-r-sm"
                    data-testid={`discovery-item-${h.heart_number}`}
                  >
                    {h.close_photo_path ? (
                      <div className="w-8 h-8 bg-[#27272A] overflow-hidden flex-shrink-0 rounded-sm">
                        <img src={getFileUrl(h.close_photo_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 bg-white/[0.06] flex items-center justify-center flex-shrink-0 rounded-sm">
                        <Heart className={`w-3 h-3 ${isVerified ? 'text-[#E63946]' : 'text-white'}`} fill={isVerified ? '#E63946' : 'white'} />
                      </div>
                    )}
                    <span className={`font-bold text-sm w-10 flex-shrink-0 ${isVerified ? 'text-[#ff6b7a]' : 'text-white'}`}>#{h.heart_number}</span>
                    <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm flex-shrink-0 ${isVerified ? 'bg-[#E63946]/20 text-[#E63946]' : 'bg-white/10 text-white/60'}`}>
                      {isVerified ? 'Vérifié' : 'Déclaré'}
                    </span>
                    <div className="flex-1 min-w-0 text-[11px] text-white/75 truncate flex items-center gap-1">
                      <MapPin className="w-2.5 h-2.5 flex-shrink-0 text-white/45" />{h.location_name || 'Paris'}
                    </div>
                    <span className="text-[10px] text-white/40 flex-shrink-0 whitespace-nowrap">{timeAgo(h.discovered_at)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default HeartsGrid;
